import React, { useEffect, useMemo, useState } from 'react';
import { useAccountLinking } from '../../auth/useAccountLinking';
import { useJWTAuth } from '../../auth/JWTAuthContext';

const defaultAttributes = {
  strength: 10,
  endurance: 10,
  coordination: 10,
  quickness: 10,
  focus: 10,
  self: 10,
};

const attributeDescriptions = {
  strength: 'Increases melee damage and contributes to overall health.',
  endurance: 'Improves maximum health and stamina to survive longer.',
  coordination: 'Improves accuracy and finesse with ranged weapons.',
  quickness: 'Improves movement and attack speed; better kiting.',
  focus: 'Improves magic potency and mana related stats.',
  self: 'Improves magic resistance and defensive willpower.',
};

// Dumb renderer for already-fetched players
const ThemePlayersGrid = ({ players, icon, setIcon }) => {
  if (!players || players.length === 0) {
    return <div className="text-gray-400 text-sm">Loading icons…</div>;
  }
  return (
    <div className="grid grid-cols-3 gap-3">
      {players.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => setIcon(p.url)}
          className={`h-30 w-30 rounded-lg border overflow-hidden flex items-center justify-center ${icon === p.url ? 'border-blue-500 bg-blue-500/10' : 'border-white/10 bg-gray-900 hover:border-white/20'}`}
          aria-label={p.name}
          title={p.name}
        >
          <img src={p.url} alt={p.name} className="h-full w-full object-contain" />
        </button>
      ))}
    </div>
  );
};

const CreateWarriorModal = ({ isOpen, onClose, onCreated }) => {
  const { user } = useAccountLinking();
  const { getAuthHeaders } = useJWTAuth();
  const username = user?.username || '';

  const [name, setName] = useState('');
  const [defaultName, setDefaultName] = useState('');
  const [attributes, setAttributes] = useState(defaultAttributes);
  const [preset, setPreset] = useState('balanced');
  const MAX_POINTS = 270;
  const baseTotal = 6 * 10; // base 10 each
  const spentPoints = Object.values(attributes).reduce((sum, v) => sum + (v - 10), 0);
  const remaining = Math.max(0, MAX_POINTS - spentPoints);
  const [step, setStep] = useState(1); // 1: starting build, 2: attributes, 3: appearance, 4: name
  const [icon, setIcon] = useState('');
  const [playerIcons, setPlayerIcons] = useState([]);

  // Prefetch theme players and images as soon as modal opens
  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    // Generate a fun placeholder name once per open
    const animals = ['Otter','Panda','Llama','Penguin','Quokka','Fox','Ferret','Badger','Falcon','Koala','Raccoon','Marmot','Yak','Gibbon','Dingo'];
    const adjectives = ['Zany','Cheeky','Bouncy','Witty','Jazzy','Spicy','Snazzy','Sassy','Brave','Nimble','Sunny','Quirky','Feisty','Swift','Dapper'];
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const funny = `${pick(adjectives)}${pick(animals).toLowerCase()}`; // single ProperCase word per server rule
    setDefaultName(funny.slice(0, 20));
    (async () => {
      try {
        const res = await fetch('/api/themes/normalized/soulwars-default/players', {
          headers: { 'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }
        });
        const data = await res.json();
        if (!mounted || !data.success) return;
        let players = data.data.players || [];
        // Prefetch images
        players.forEach((p) => { const img = new Image(); img.src = p.url; });
        //Limit the icons to 9, if there are 9
        if (players.length > 9) {
          players = players.slice(0, 9);
        }
        setPlayerIcons(players);
        // Set default icon if none selected yet
        if (!icon && players[0]) setIcon(players[0].url);
      } catch (e) {
        console.error('Failed to load theme players', e);
      }
    })();
    return () => { mounted = false; };
  }, [isOpen]);

  const derived = useMemo(() => {
    const { endurance, self } = attributes;
    const health = Math.floor((endurance || 0) / 2);
    const stamina = endurance || 0;
    const mana = self || 0;
    return { health, stamina, mana };
  }, [attributes]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const finalName = (name || defaultName || '').trim();

  if (!isOpen) return null;

  const resetForm = () => {
    setStep(1);
    setPreset('balanced');
    setAttributes({ strength: 55, endurance: 55, coordination: 55, quickness: 55, focus: 55, self: 55 });
    setName('');
    setIcon(null);
    setError('');
    setSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side name validation: ProperCase alpha, 2-20 letters, single word
    const properCase = /^[A-Z][a-z]{1,19}$/;
    const BAD_WORDS = ['fuck','shit','bitch','cunt','nigger','nigga','fag','whore','slut','rape','hitler','admin','moderator','dev','staff'];
    if (!finalName || !properCase.test(finalName)) {
      setError('Name must be 2-20 letters, single word, Proper Case (e.g., Aralon).');
      return;
    }
    if (BAD_WORDS.some(w => finalName.toLowerCase().includes(w))) {
      setError('Inappropriate or reserved name.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        userId: user?.id,
        name: finalName,
        ...attributes,
        strategy_aggression: 50,
        strategy_looting: 50,
        strategy_positioning: 50,
        icon,
      };

      const res = await fetch('/api/heroes/create', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to create hero');
      }

      resetForm();
      onCreated?.(data.data);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="modal-container relative w-full max-w-lg mx-auto bg-gray-800 rounded-xl border border-white/10 shadow-2xl p-4 sm:p-6 my-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
        {/* Larger slider styling and mobile scroll fixes */}
        <style>{`
          .slider-lg { height: 10px; }
          .slider-lg::-webkit-slider-thumb { -webkit-appearance: none; height: 24px; width: 24px; border-radius: 9999px; background: #facc15; border: 2px solid #eab308; }
          .slider-lg::-moz-range-thumb { height: 24px; width: 24px; border-radius: 9999px; background: #facc15; border: 2px solid #eab308; }
          .slider-lg::-ms-thumb { height: 24px; width: 24px; border-radius: 9999px; background: #facc15; border: 2px solid #eab308; }
          .slider-lg::-webkit-slider-runnable-track { height: 10px; border-radius: 9999px; background: #374151; }
          .slider-lg::-moz-range-track { height: 10px; border-radius: 9999px; background: #374151; }
          .slider-lg::-ms-track { height: 10px; border-radius: 9999px; background: #374151; border-color: transparent; color: transparent; }
          
          /* Mobile scroll fixes */
          @media (max-width: 768px) {
            .modal-container {
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
              max-height: calc(100vh - 2rem);
              max-height: calc(100dvh - 2rem); /* Dynamic viewport height for mobile browsers */
            }
            
            /* Fix for Phantom and other mobile browsers */
            .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
            }
          }
        `}</style>
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-xl font-bold text-white">Create Hero</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-white">✕</button>
        </div>

        {/* Step 1: starting build */}
        {step === 1 && (
          <div className="space-y-4 overflow-y-auto">
            <div>
              <div className="text-sm text-gray-300 mb-2">Starting build</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  {
                    key: 'balanced',
                    label: 'Balanced',
                    values: { strength: 55, endurance: 55, coordination: 55, quickness: 55, focus: 55, self: 55 },
                    blurb: 'Even distribution for all playstyles.',
                  },
                  {
                    key: 'glass',
                    label: 'Glass Cannon',
                    values: { strength: 80, endurance: 50, coordination: 70, quickness: 60, focus: 40, self: 30 },
                    blurb: 'High damage output, low durability.',
                  },
                  {
                    key: 'tank',
                    label: 'Tank',
                    values: { strength: 70, endurance: 90, coordination: 35, quickness: 25, focus: 50, self: 60 },
                    blurb: 'Maximum survivability and frontline presence.',
                  },
                  {
                    key: 'skirmisher',
                    label: 'Skirmisher',
                    values: { strength: 60, endurance: 55, coordination: 70, quickness: 85, focus: 35, self: 25 },
                    blurb: 'Speed and finesse with strong mobility.',
                  },
                  {
                    key: 'custom',
                    label: 'Custom',
                    values: { strength: 10, endurance: 10, coordination: 10, quickness: 10, focus: 10, self: 10 },
                    blurb: 'Start from scratch and assign points yourself.',
                  },
                ].map(p => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => { setPreset(p.key); setAttributes(p.values); }}
                    className={`text-left bg-gray-900 border rounded-lg px-4 py-3 transition-colors ${preset === p.key ? 'border-blue-500' : 'border-white/10 hover:border-white/20'}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon placeholder */}
                      <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-gray-400 text-sm">⚔️</span>
                      </div>
                      
                      {/* Text content */}
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-bold text-lg tracking-wide">{p.label}</div>
                        <div className="text-sm text-gray-400 leading-relaxed">{p.blurb}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center gap-3 pt-2">
              <div></div>
              <div className="text-sm text-gray-400">Step 1 of 4</div>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 2: attributes with tooltips */}
        {step === 2 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="text-gray-300">Points to assign</div>
              <div className="text-white font-semibold">{remaining}</div>
            </div>

            <div className="space-y-2">
              {Object.entries(attributes).map(([key, value]) => (
                <div key={key} className="bg-gray-900/50 border border-white/10 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-0">
                    <span className="capitalize text-yellow-300 text-base sm:text-lg font-semibold flex items-center gap-2">
                      {key}
                      <button type="button" className="w-5 h-5 rounded-full bg-gray-700 text-white text-xs" title={attributeDescriptions[key]}>?</button>
                    </span>
                    <span className="text-yellow-300 text-lg sm:text-xl font-extrabold">{value}</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    step={1}
                    value={value}
                    onChange={(e) => {
                      const next = parseInt(e.target.value, 10);
                      const nextSpent = spentPoints - (value - 10) + (next - 10);
                      if (nextSpent <= MAX_POINTS) {
                        setAttributes((prev) => ({ ...prev, [key]: next }));
                      }
                    }}
                    className="w-full accent-yellow-400 slider-lg"
                  />
                </div>
              ))}
            </div>

            {/* Derived stats */}
            <div className="grid grid-cols-3 gap-1 text-xs text-gray-300">
              <div className="bg-gray-900 border border-white/10 rounded-lg p-2 text-center">
                <div className="text-gray-400">Health</div>
                <div className="text-white font-semibold">{derived.health}</div>
              </div>
              <div className="bg-gray-900 border border-white/10 rounded-lg p-2 text-center">
                <div className="text-gray-400">Stamina</div>
                <div className="text-white font-semibold">{derived.stamina}</div>
              </div>
              <div className="bg-gray-900 border border-white/10 rounded-lg p-2 text-center">
                <div className="text-gray-400">Mana</div>
                <div className="text-white font-semibold">{derived.mana}</div>
              </div>
            </div>

            <div className="flex justify-between items-center gap-3 pt-0">
              <button type="button" onClick={() => setStep(1)} className="px-4 py-2 rounded-lg text-gray-300 hover:text-white">Back</button>
              <div className="text-sm text-gray-400">Step 2 of 4</div>
              <button type="button" onClick={() => setStep(3)} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold">Next</button>
            </div>
          </div>
        )}

        {/* Step 3: appearance */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-300 mb-2">Select appearance</div>
              <ThemePlayersGrid players={playerIcons} icon={icon} setIcon={setIcon} />
            </div>
            <div className="flex justify-between items-center gap-3 pt-2">
              <button type="button" onClick={() => setStep(2)} className="px-4 py-2 rounded-lg text-gray-300 hover:text-white">Back</button>
              <div className="text-sm text-gray-400">Step 3 of 4</div>
              <button type="button" onClick={() => setStep(4)} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold">Next</button>
            </div>
          </div>
        )}

        {/* Step 4: name confirmation */}
        {step === 4 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Hero Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={defaultName || (username ? `e.g. ${username}` : 'Enter a unique hero name')}
                maxLength={50}
                className="w-full bg-gray-900 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
              <div className="mt-1 text-xs text-gray-500">Names are public and must be unique.</div>
            </div>

            <div className="bg-gray-900 border border-white/10 rounded-lg p-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded bg-gray-800 border border-white/10 overflow-hidden flex items-center justify-center">
                  {icon ? <img src={icon} alt="icon" className="h-full w-full object-contain" /> : <div className="w-full h-full bg-gray-700" />}
                </div>
                <div>
                  <div className="text-white font-semibold">{name || defaultName || 'Unnamed Hero'}</div>
                  <div className="text-xs text-gray-400">Level 1</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                {Object.entries(attributes).map(([k, v]) => (
                  <div key={k} className="bg-gray-800 rounded p-2">
                    <div className="text-yellow-300 font-bold text-base">{v}</div>
                    <div className="text-gray-400 capitalize">{k}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs mt-2">
                <div className="bg-gray-800 rounded p-2">
                  <div className="text-white font-bold">{derived.health}</div>
                  <div className="text-gray-400">Health</div>
                </div>
                <div className="bg-gray-800 rounded p-2">
                  <div className="text-white font-bold">{derived.stamina}</div>
                  <div className="text-gray-400">Stamina</div>
                </div>
                <div className="bg-gray-800 rounded p-2">
                  <div className="text-white font-bold">{derived.mana}</div>
                  <div className="text-gray-400">Mana</div>
                </div>
              </div>
            </div>

            {error && <div className="text-sm text-red-400">{error}</div>}

            <div className="flex justify-between items-center gap-3 pt-2">
              <button type="button" onClick={() => setStep(3)} className="px-4 py-2 rounded-lg text-gray-300 hover:text-white">Back</button>
              <div className="text-sm text-gray-400">Step 4 of 4</div>
              <button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg font-semibold"
              >
                {submitting ? 'Creating...' : 'Create Hero'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateWarriorModal;


